import { supabase } from '@/lib/supabaseClient';
import { ensureAuthenticatedSession } from '@/utils/authentication';
import { getReservationAreasByIds } from './reservationAreaService';
import { Payment } from '@/types';
import { isRecord, toOptionalNumber, toOptionalString } from '@/utils/typeGuards';

interface ReservationSummaryForPayment {
  id: string;
  start_time: string | null;
  end_time: string | null;
  payment_id: string | null;
  area_id?: string | null;
  area?: {
    id: string;
    name?: string | null;
  } | null;
}

export interface PaymentRecord {
  id: string;
  created_at: string;
  user_id: string | null;
  stripe_session_id: string | null;
  stripe_payment_intent: string | null;
  amount_total: number | null;
  currency: string | null;
  status: string | null;
  raw: Record<string, unknown> | null;
}

export interface PaymentWithReservation extends PaymentRecord {
  reservation?: ReservationSummaryForPayment | null;
}

const toPaymentRecord = (value: unknown): PaymentRecord | null => {
  if (!isRecord(value)) {
    return null;
  }

  const id = toOptionalString(value.id);
  if (!id) {
    return null;
  }

  return {
    id,
    created_at: toOptionalString(value.created_at) ?? '',
    user_id: toOptionalString(value.user_id),
    stripe_session_id: toOptionalString(value.stripe_session_id),
    stripe_payment_intent: toOptionalString(value.stripe_payment_intent),
    amount_total: toOptionalNumber(value.amount_total),
    currency: toOptionalString(value.currency),
    status: toOptionalString(value.status),
    raw: isRecord(value.raw) ? value.raw : null,
  };
};

export async function getPaymentByStripeSession(
  sessionId: string
): Promise<PaymentRecord | null> {
  await ensureAuthenticatedSession();
  const result = await supabase
    .from('payments')
    .select('*')
    .eq('stripe_session_id', sessionId)
    .maybeSingle();

  if (result.error) {
    throw result.error;
  }

  return toPaymentRecord(result.data);
}

export interface WaitForPaymentOptions {
  maxAttempts?: number;
  intervalMs?: number;
}

export async function waitForPaymentConfirmation(
  sessionId: string,
  options: WaitForPaymentOptions = {}
): Promise<PaymentRecord | null> {
  const { maxAttempts = 10, intervalMs = 2000 } = options;

  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    const payment = await getPaymentByStripeSession(sessionId);
    if (payment) {
      return payment;
    }

    await new Promise(resolve => setTimeout(resolve, intervalMs));
  }

  return null;
}

export async function getPaymentsForCurrentUser(): Promise<PaymentWithReservation[]> {
  const session = await ensureAuthenticatedSession();

  const { data, error } = await supabase
    .from('payments')
    .select('*')
    .eq('user_id', session.user.id)
    .order('created_at', { ascending: false });

  if (error) {
    throw error;
  }

  const payments = ((data as PaymentRecord[]) ?? []).map(payment => ({
    ...payment,
  }));

  const paymentIds = payments.map(payment => payment.id).filter(Boolean);

  if (paymentIds.length === 0) {
    return payments;
  }

  const { data: reservationsData, error: reservationsError } = await supabase
    .from('reservations')
    .select('id, start_time, end_time, payment_id, area_id')
    .in('payment_id', paymentIds);

  if (reservationsError) {
    throw reservationsError;
  }

  const reservations = (reservationsData ?? []) as Array<{
    id: string;
    start_time: string | null;
    end_time: string | null;
    payment_id: string | null;
    area_id: string | null;
  }>;

  const areaIds = Array.from(
    new Set(
      reservations
        .map(reservation => reservation.area_id)
        .filter((areaId): areaId is string => Boolean(areaId && areaId.trim().length > 0))
    )
  );

  const areaMap = new Map<string, { id: string; name?: string | null }>();
  if (areaIds.length > 0) {
    const areas = await getReservationAreasByIds(areaIds);
    areas.forEach(area => {
      if (!area?.id) return;
      areaMap.set(String(area.id), {
        id: String(area.id),
        name: area.name ?? null,
      });
    });
  }

  const reservationByPaymentId = new Map<string, ReservationSummaryForPayment>();

  reservations.forEach(reservation => {
    if (!reservation.payment_id) return;
    const areaSummary = reservation.area_id ? areaMap.get(reservation.area_id) ?? null : null;
    reservationByPaymentId.set(reservation.payment_id, {
      id: String(reservation.id ?? ''),
      start_time: reservation.start_time ?? null,
      end_time: reservation.end_time ?? null,
      payment_id: reservation.payment_id ?? null,
      area: areaSummary,
    });
  });

  return payments.map(payment => ({
    ...payment,
    reservation: reservationByPaymentId.get(payment.id) ?? null,
  }));
}

type DbCommunityPayment = {
  id: string;
  community_id: string;
  user_id: string;
  description: string;
  amount: number;
  due_date: string;
  paid_date: string | null;
  type: Payment['type'];
  status: Payment['status'];
  attachments: Array<{ name?: string; url?: string; path?: string }> | null;
  created_at?: string | null;
  updated_at?: string | null;
};

type CreateCommunityPaymentInput = {
  id?: string;
  communityId: string;
  userId: string;
  description: string;
  amount: number;
  dueDate: string;
  paidDate?: string | null;
  type: Payment['type'];
  status: Payment['status'];
  attachments?: Array<{ name: string; url: string; path?: string }>;
};

type UpdateCommunityPaymentInput = Partial<{
  description: string;
  amount: number;
  dueDate: string;
  paidDate: string | null;
  type: Payment['type'];
  status: Payment['status'];
  attachments: Array<{ name: string; url: string; path?: string }>;
}>;

const COMMUNITY_PAYMENT_SELECT =
  'id, community_id, user_id, description, amount, due_date, paid_date, type, status, attachments, created_at, updated_at';

const normalizeAttachments = (
  attachments: Array<{ name?: string; url?: string; path?: string }> | null | undefined
) =>
  (attachments ?? [])
    .filter((item) => item && typeof item.name === 'string' && typeof item.url === 'string')
    .map((item) => ({
      name: item.name as string,
      url: item.url as string,
      path: typeof item.path === 'string' ? item.path : undefined,
    }));

const mapDbCommunityPayment = (row: DbCommunityPayment): Payment => ({
  id: row.id,
  communityId: row.community_id,
  userId: row.user_id,
  description: row.description,
  amount: Number(row.amount),
  dueDate: row.due_date,
  paidDate: row.paid_date ?? undefined,
  type: row.type,
  status: row.status,
  isPaid: row.status === 'paid' || row.status === 'paid_with_surcharge' || Boolean(row.paid_date),
  attachments: normalizeAttachments(row.attachments),
  createdAt: row.created_at ?? undefined,
  updatedAt: row.updated_at ?? undefined,
});

export async function getCommunityPaymentRecords(options: { communityIds?: string[] } = {}): Promise<Payment[]> {
  await ensureAuthenticatedSession();

  const communityIds = Array.from(new Set((options.communityIds ?? []).filter(Boolean)));
  let query = supabase
    .from('community_payments')
    .select(COMMUNITY_PAYMENT_SELECT)
    .order('created_at', { ascending: false });

  if (communityIds.length > 0) {
    query = query.in('community_id', communityIds);
  }

  const { data, error } = await query;

  if (error) throw error;

  return ((data as DbCommunityPayment[]) ?? []).map(mapDbCommunityPayment);
}

export async function createCommunityPaymentRecord(input: CreateCommunityPaymentInput): Promise<Payment> {
  await ensureAuthenticatedSession();

  const payload = {
    ...(input.id ? { id: input.id } : {}),
    community_id: input.communityId,
    user_id: input.userId,
    description: input.description,
    amount: input.amount,
    due_date: input.dueDate,
    paid_date: input.paidDate ?? null,
    type: input.type,
    status: input.status,
    attachments: input.attachments ?? [],
  };

  const { data, error } = await supabase
    .from('community_payments')
    .insert(payload)
    .select(COMMUNITY_PAYMENT_SELECT)
    .single();

  if (error) throw error;

  return mapDbCommunityPayment(data as DbCommunityPayment);
}

export async function updateCommunityPaymentRecord(
  paymentId: string,
  input: UpdateCommunityPaymentInput
): Promise<Payment> {
  await ensureAuthenticatedSession();

  const payload: Partial<DbCommunityPayment> = {};

  if (input.description !== undefined) payload.description = input.description;
  if (input.amount !== undefined) payload.amount = input.amount;
  if (input.dueDate !== undefined) payload.due_date = input.dueDate;
  if (input.paidDate !== undefined) payload.paid_date = input.paidDate || null;
  if (input.type !== undefined) payload.type = input.type;
  if (input.status !== undefined) payload.status = input.status;
  if (input.attachments !== undefined) payload.attachments = input.attachments;

  const { data, error } = await supabase
    .from('community_payments')
    .update(payload)
    .eq('id', paymentId)
    .select(COMMUNITY_PAYMENT_SELECT)
    .single();

  if (error) throw error;

  return mapDbCommunityPayment(data as DbCommunityPayment);
}

export async function deleteCommunityPaymentRecord(paymentId: string): Promise<void> {
  await ensureAuthenticatedSession();

  const { error } = await supabase
    .from('community_payments')
    .delete()
    .eq('id', paymentId);

  if (error) throw error;
}
